import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { OnboardingChecklistService } from '../onboarding-checklist/onboarding-checklist.service';
import { TokenEncryptionService } from '../Services/token-encryption.service';
import { hasCompanyRole } from './role.utils';
import { sysConfig } from '../sysConfig';
import {
  ActivationJourneyService,
  activationStepToSlug,
} from '../onboarding-checklist/activation-journey.service';
import { ActivationDestinationService } from '../onboarding-checklist/activation-destination.service';
import { ExampleWorkdayService } from '../example-workday/example-workday.service';

export const setupGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const onboardingService = inject(OnboardingChecklistService);
  const tokenEncryptionService = inject(TokenEncryptionService);
  const activationJourney = inject(ActivationJourneyService);
  const activationDestination = inject(ActivationDestinationService);
  const exampleWorkday = inject(ExampleWorkdayService);

  const user = tokenEncryptionService.getEncryptedItem<any>('user') || tokenEncryptionService.getEncryptedItem<any>('logedInUser');
  if (!hasCompanyRole(user)) {
    void router.navigate(['/dashboard'], { replaceUrl: true });
    return of(false);
  }

  // Profile → Journey uses ?from=menu; Restart journey uses ?from=restart.
  // Session latch covers beat/query sync that can drop the query param mid-funnel.
  const urlQuery = (state.url || '').includes('?')
    ? new URLSearchParams((state.url || '').split('?')[1] || '')
    : null;
  const fromParam = route.queryParamMap.get('from') || urlQuery?.get('from') || '';
  const latchedReentry = activationJourney.getIntentionalReentry();
  const allowReentry =
    fromParam === 'menu' ||
    fromParam === 'restart' ||
    latchedReentry === 'menu' ||
    latchedReentry === 'restart';
  if (fromParam === 'menu' || fromParam === 'restart') {
    activationJourney.beginIntentionalReentry(fromParam);
  }

  // Existing customers who completed classic onboarding should not enter /setup —
  // unless they explicitly reopened Journey or Restarted.
  if (
    sysConfig.activationJourneyEnabled &&
    onboardingService.isPermanentlyCompleted() &&
    !allowReentry
  ) {
    const js = activationJourney.getState();
    if (
      js.completedSteps.length === 0 &&
      !js.goal &&
      !js.privateTestEmployeeId &&
      !js.firstEmployeeId
    ) {
      void router.navigate(['/dashboard'], { replaceUrl: true });
      return of(false);
    }
  }

  // Activation lane (local/staging): allow /setup while flag is on, but degrade
  // invalid/future deep links to the nearest valid destination.
  if (sysConfig.activationJourneyEnabled) {
    const url = state.url || '/setup';
    const requestedPath = (url.split('?')[0] || '').replace(/\/+$/, '') || '/';

    // Example side-path: allow when session is active or entry query can rehydrate.
    if (requestedPath === '/setup/example-workday') {
      const entryQuery = route.queryParamMap.get('entry');
      if (exampleWorkday.canAccessExampleRoute(entryQuery)) {
        return of(true);
      }
      const dest = activationDestination.resolve({
        state: activationJourney.getState(),
        requestedPath: url,
        allowReentry,
      });
      void router.navigate([dest.route], {
        queryParams: dest.queryParams || {},
        replaceUrl: true,
      });
      return of(false);
    }

    // Always allow the URL for the step the journey is already on — otherwise
    // the destination resolver cancels pushState and the address bar never moves.
    const journeyState = activationJourney.getState();
    const currentStepPath = `/setup/${activationStepToSlug(journeyState.currentStep)}`;
    if (requestedPath === currentStepPath || requestedPath === '/setup') {
      return of(true);
    }

    const dest = activationDestination.resolve({
      state: journeyState,
      requestedPath: url,
      allowReentry,
    });
    const targetPath = (dest.route || '').replace(/\/+$/, '') || '/';
    if (dest.degradedFrom && requestedPath.startsWith('/setup') && requestedPath !== targetPath) {
      void router.navigateByUrl(dest.route, { replaceUrl: true });
      return of(false);
    }
    // Completed/skipped Bare Back: destination is dashboard with degradedFrom.
    if (
      requestedPath.startsWith('/setup') &&
      targetPath === '/dashboard' &&
      !allowReentry
    ) {
      void router.navigateByUrl(dest.route, { replaceUrl: true });
      return of(false);
    }
    return of(true);
  }

  return onboardingService.checkEligibility().pipe(
    map((eligible: boolean) => {
      if (!eligible) {
        void router.navigate(['/dashboard'], { replaceUrl: true });
        return false;
      }
      return true;
    }),
    catchError(() => {
      void router.navigate(['/dashboard'], { replaceUrl: true });
      return of(false);
    })
  );
};
